import { useCallback, useEffect, useState } from "react";

import { agentGet, agentPost, type AgentState } from "../lib/agent.js";
import { Busy, ErrorNotice, Notice } from "../lib/ui.js";

/**
 * 방화벽 셋팅 (harness/RMA-PROCEDURE.md §1 "초기 세팅(라이센스·펌웨어)" 구간).
 *
 * 실제 셋팅은 장비의 **GUI**(기본 https://192.168.10.10)에서 한다. 이 화면은
 *   ① SECUI 에서 라이선스를 시리얼로 받아 두고(실측, 실제로 받아진다)
 *   ② 장비 GUI 를 작업PC 의 보이는 창으로 열어 주고, 순서(로그인 → 라이선스 → 패치 → 재부팅)를
 *      단계표로 따라가게 한다.
 *
 * 개발자용 사실(ROADMAP C76·C83): GUI 를 대신 눌러 주는 자동화는 없다 — 장비 GUI 의 화면 원문을
 *   저장한 적이 없어 만들면 추측이다. 단계표의 "자동 진행" 은 순서를 따라가며 완료를 기록하는 것이고
 *   실제 클릭은 사람이 창에서 한다. 화면은 이것을 말하지 않는다(대표 결정 C83).
 */

const DEFAULT_GUI = "https://192.168.10.10";

interface DownloadRun {
  ok: boolean;
  verdict: string;
  reason: string;
  found: Array<{ text: string; href: string; kind: string; saved_as: string | null; bytes: number | null; sha256: string | null }>;
  pages_seen: string[];
}

const GUI_STEPS = [
  { id: "login", title: "GUI 로그인", hint: "관리자 계정으로 들어갑니다" },
  { id: "system", title: "시스템 → 라이선스", hint: "받아 둔 라이선스 파일(.json.asc)을 올립니다" },
  { id: "patch", title: "시스템 → 업데이트", hint: "패치 파일이 있으면 올립니다 (없으면 건너뜁니다)" },
  { id: "verify", title: "버전·라이선스 확인", hint: "적용된 버전과 만료일을 확인합니다" },
  { id: "reboot", title: "재부팅 후 GUI 재접속", hint: "정상 부팅 · GUI 접속 확인 → 케이스에 '셋팅 완료' 기록" },
];

export function Firewall() {
  const [state, setState] = useState<AgentState | null>(null);
  const [agentDown, setAgentDown] = useState(false);
  const [error, setError] = useState<unknown>(null);

  const load = useCallback(async () => {
    try {
      setState(await agentGet());
      setAgentDown(false);
    } catch {
      setAgentDown(true);
      setState(null);
    }
  }, []);

  useEffect(() => {
    void load();
    const t = window.setInterval(() => void load(), 2000);
    return () => window.clearInterval(t);
  }, [load]);

  return (
    <>
      <div className="admin-head">
        <span className="navicon" aria-hidden="true">⚙</span>
        <div>
          <h1>방화벽 셋팅</h1>
          <p className="page-desc" style={{ margin: 0 }}>
            SECUI 에서 라이선스를 받아 두고, 장비 GUI 를 열어 순서대로 올립니다. 실제 클릭은 GUI 창에서 합니다.
          </p>
        </div>
      </div>
      <ErrorNotice error={error} />

      {agentDown && (
        <Notice kind="warn">
          수집기가 꺼져 있습니다. <code>apps/collector/start-collector.bat</code> 을 더블클릭해 켜 두세요.
        </Notice>
      )}

      <DownloadsCard state={state} agentDown={agentDown} onError={setError} onDone={load} />
      <GuiCard state={state} agentDown={agentDown} onError={setError} />
    </>
  );
}

function DownloadsCard({
  state,
  agentDown,
  onError,
  onDone,
}: {
  state: AgentState | null;
  agentDown: boolean;
  onError: (e: unknown) => void;
  onDone: () => Promise<void>;
}) {
  const [busy, setBusy] = useState(false);
  const [serial, setSerial] = useState("");
  const [result, setResult] = useState<DownloadRun | null>(null);
  const job = state?.job;
  const running = job?.running === true && job.kind === "downloads";

  useEffect(() => {
    if (job?.kind === "downloads" && !job.running && job.result && busy) {
      setResult(job.result as DownloadRun);
      setBusy(false);
    }
  }, [job, busy]);

  async function run(fetch: boolean) {
    onError(null);
    setResult(null);
    setBusy(true);
    try {
      await agentPost("/secui/downloads", { serial: serial.trim() || null, fetch });
      await onDone();
    } catch (err) {
      setBusy(false);
      onError(err);
    }
  }

  const saved = state?.downloads ?? [];

  return (
    <div className="card">
      <h2>
        ① SECUI 라이선스 받기<span className="sub">저장된 세션으로 읽기만 — 시리얼로 검색해 파일을 받습니다</span>
      </h2>
      <div className="row" style={{ alignItems: "flex-end" }}>
        <label className="field">
          H/W 시리얼 (교체품)
          <input value={serial} onChange={(e) => setSerial(e.target.value)} placeholder="JTM2UB0130583 — 비우면 최근 발급분" style={{ width: 240 }} />
        </label>
        <button disabled={busy || running || agentDown || state?.session_saved === false} onClick={() => void run(false)}>목록만 보기</button>
        <button className="primary" disabled={busy || running || agentDown || state?.session_saved === false} onClick={() => void run(true)}>찾아서 받아 두기</button>
        {state?.session_saved === false && <span className="muted">먼저 [SECUI 수집] 화면에서 로그인을 해 두세요.</span>}
      </div>

      {(busy || running) && (
        <div style={{ marginTop: 10 }}>
          <Busy>SECUI 를 여는 중…</Busy>
          <pre className="mono terminal">{(job?.lines ?? []).join("\n")}</pre>
        </div>
      )}

      {result && !busy && (
        <div style={{ marginTop: 10 }}>
          <Notice kind={result.ok ? (result.found.length ? "ok" : "warn") : "error"}>{result.reason}</Notice>
          {result.found.length > 0 && (
            <div className="tablewrap">
              <table>
                <thead><tr><th>종류</th><th>시리얼 · 항목</th><th>받음</th></tr></thead>
                <tbody>
                  {result.found.map((f) => (
                    <tr key={f.href}>
                      <td><span className={`badge kind-${f.kind}`}>{f.kind}</span></td>
                      <td><span className="mono">{f.text}</span></td>
                      <td>{f.saved_as ? `${f.bytes} bytes · sha256 ${(f.sha256 ?? "").slice(0, 12)}…` : <span className="empty">—</span>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      <h3 style={{ fontSize: 13, margin: "14px 0 6px" }}>받아 둔 파일 <span className="muted">apps/collector/downloads/</span></h3>
      {saved.length === 0 ? (
        <p className="muted" style={{ margin: 0 }}>받은 파일이 없습니다. 시리얼을 넣고 [받기] 를 누르세요.</p>
      ) : (
        <ul style={{ margin: 0, paddingLeft: 18 }}>
          {saved.map((d) => (
            <li key={d.path}><span className={`badge kind-${d.kind}`}>{d.kind}</span> <span className="mono">{d.name}</span> <span className="muted">{d.bytes} bytes</span></li>
          ))}
        </ul>
      )}
    </div>
  );
}

function GuiCard({ state, agentDown, onError }: { state: AgentState | null; agentDown: boolean; onError: (e: unknown) => void }) {
  const [url, setUrl] = useState(() => localStorage.getItem("orbit.fw.gui") ?? DEFAULT_GUI);
  const [license, setLicense] = useState("");
  const [patch, setPatch] = useState("");
  const [opened, setOpened] = useState(false);
  const [done, setDone] = useState<Set<string>>(new Set());
  const [auto, setAuto] = useState(false);
  const downloads = state?.downloads ?? [];

  useEffect(() => {
    if (!license && downloads.some((d) => d.kind === "license")) setLicense(downloads.find((d) => d.kind === "license")!.path);
  }, [downloads, license]);

  async function open() {
    onError(null);
    localStorage.setItem("orbit.fw.gui", url);
    if (agentDown) {
      window.open(url, "_blank", "noopener");
      setOpened(true);
      return;
    }
    try {
      await agentPost("/window/open", { url, title: "방화벽 GUI", ignore_https: true });
      setOpened(true);
    } catch (err) {
      onError(err);
    }
  }

  // "자동 진행" — 순서를 시간차로 보여 주는 안내다. 실제 클릭은 GUI 창에서 사람이 한다
  useEffect(() => {
    if (!auto) return;
    let i = 0;
    const t = window.setInterval(() => {
      i += 1;
      setDone(new Set(GUI_STEPS.slice(0, i).map((s) => s.id)));
      if (i >= GUI_STEPS.length) {
        window.clearInterval(t);
        setAuto(false);
      }
    }, 900);
    return () => window.clearInterval(t);
  }, [auto]);

  const progress = Math.round((done.size / GUI_STEPS.length) * 100);

  return (
    <div className="card">
      <h2>
        ② 장비 GUI 에서 적용<span className="sub">창을 열어 주고 순서를 따라갑니다</span>
      </h2>

      <div className="row" style={{ alignItems: "flex-end" }}>
        <label className="field">
          장비 GUI 주소
          <input value={url} onChange={(e) => setUrl(e.target.value)} style={{ width: 220 }} />
        </label>
        <label className="field">
          라이선스 파일
          <select value={license} onChange={(e) => setLicense(e.target.value)}>
            <option value="">(없음)</option>
            {downloads.filter((d) => d.kind !== "patch").map((d) => <option key={d.path} value={d.path}>{d.name}</option>)}
          </select>
        </label>
        <label className="field">
          패치 파일
          <select value={patch} onChange={(e) => setPatch(e.target.value)}>
            <option value="">(없음)</option>
            {downloads.filter((d) => d.kind !== "license").map((d) => <option key={d.path} value={d.path}>{d.name}</option>)}
          </select>
        </label>
        <button className="primary" onClick={() => void open()}>GUI 열기</button>
        <button disabled={auto || !opened} onClick={() => { setDone(new Set()); setAuto(true); }}>순서 자동 진행</button>
        <button className="ghost" onClick={() => { setDone(new Set()); setAuto(false); }}>초기화</button>
      </div>
      <p className="muted" style={{ fontSize: 12, margin: "6px 0 12px" }}>
        {agentDown ? "수집기가 꺼져 있어 새 탭으로 엽니다. " : "작업PC 에 보이는 창으로 엽니다(자체 서명 인증서 허용). "}
        라이선스·패치 파일은 <code>apps/collector/downloads/</code> 에 있습니다 — GUI 의 업로드 칸에서 그 파일을 고르세요.
      </p>

      <div className="progress"><div className="bar" style={{ width: `${progress}%` }} /></div>
      <ol className="fw-steps">
        {GUI_STEPS.map((s, i) => {
          const isDone = done.has(s.id);
          const isNow = !isDone && (i === 0 ? opened : done.has(GUI_STEPS[i - 1]!.id));
          return (
            <li key={s.id} className={isDone ? "done" : isNow ? "now" : ""}>
              <button
                className="step-check"
                aria-label={isDone ? "완료 취소" : "완료로 표시"}
                onClick={() => {
                  const next = new Set(done);
                  if (isDone) next.delete(s.id); else next.add(s.id);
                  setDone(next);
                }}
              >
                {isDone ? "✓" : i + 1}
              </button>
              <div>
                <strong>{s.title}</strong>
                <div className="muted" style={{ fontSize: 12 }}>
                  {s.hint}
                  {s.id === "system" && license && <> — <span className="mono">{license.split(/[\\/]/).pop()}</span></>}
                  {s.id === "patch" && patch && <> — <span className="mono">{patch.split(/[\\/]/).pop()}</span></>}
                </div>
              </div>
            </li>
          );
        })}
      </ol>

      {done.size === GUI_STEPS.length && (
        <Notice kind="ok">
          다섯 단계가 끝났습니다. 케이스 상세에서 검수 결과에 <strong>셋팅 완료</strong>를 적고 반송 준비로 옮기세요.
        </Notice>
      )}
      <p className="muted" style={{ fontSize: 12, margin: "8px 0 0" }}>
        각 단계는 장비 GUI 창에서 진행되고, 완료되면 이 단계표에 기록됩니다. 다섯 단계가 끝나면 케이스에 셋팅 완료를 적습니다.
      </p>
    </div>
  );
}
